import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TopicCategory, TOPIC_CATEGORY_LABELS, getAllTopicCategories } from '../constants/topicCategories';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  Search as MagnifyingGlassIcon,
  Refresh as ArrowPathIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudSync as CloudSyncIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  AutoAwesome as PolishIcon
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { Tabs } from '../components/ui/Tabs';
import { PageHeader } from '../components/ui/PageHeader';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { Dropdown } from '../components/ui/Dropdown';
import CompetitorManagement from '../components/CompetitorManagement';
import CompetitorModal from '../components/CompetitorModal';
import SalesPlaybookManagement from '../components/SalesPlaybookManagement';
import CompanyContextCard from '../components/CompanyContextCard';
import SeededOnboardingReview, { landmineDraftVersion } from '../components/SeededOnboardingReview';
import ReadinessStrip from '../components/kb/ReadinessStrip';
import NeedsAttentionQueue from '../components/kb/NeedsAttentionQueue';
import ImportsAndDrafts from '../components/kb/ImportsAndDrafts';
import QuickTestBox from '../components/kb/QuickTestBox';
import AddContentMenu, { type AddContentType } from '../components/kb/AddContentMenu';
import { KnowledgeSectionHeading } from '../components/kb/KnowledgePrimitives';
import KbAuditReviewPanel from '../components/kb/KbAuditReviewPanel';
import KbAuditLiveSyncNotice from '../components/kb/KbAuditLiveSyncNotice';
import { useKbReadiness, type NeedsAttentionSection } from '../hooks/useKbReadiness';
import { useKbAuditLiveSync } from '../hooks/useKbAuditLiveSync';
import { isOnboardingReviewSection } from '../types/onboarding';
import { uiConfig } from '../config';
import { mergeGeneratedVariations, isDuplicateKeyError } from '../utils/knowledgeBaseHelpers';
import {
  getObjectionsGrouped,
  getFAQsGrouped,
  addObjection,
  updateObjection,
  deleteObjection,
  addObjectionVariation,
  addFAQ,
  addFAQVariation,
  updateFAQ,
  deleteFAQ,
  generateObjectionVariations,
  generateFAQVariations,
  polishKnowledgeFields,
  getLandmines,
  addLandmine,
  reviewLandmine,
  updateLandmine,
  deleteLandmine,
  toggleLandmineStatus,
  generateLandmineVariations,
  getCompetitors,
  deleteCompetitor,
  searchDictionary,
  type DictionaryItem,
  type DictionaryVariation,
  type KnowledgePolishField,
  type PaginationInfo,
  type LandmineItem,
  type CompetitorItem,
  retryKnowledgeGeneratorTask,
  rescanKnowledgeGeneratorTask,
  openKnowledgeGeneratorDraft
} from '../services/apiService';

interface QuestionGroup extends DictionaryItem {
  isExpanded?: boolean;
}

type KbSection = 'overview' | 'objections' | 'faqs' | 'landmines' | 'competitors' | 'playbooks';
const VALID_KB_SECTIONS: KbSection[] = ['overview', 'objections', 'faqs', 'landmines', 'competitors', 'playbooks'];
const sectionFromUrl = (value: string | null): KbSection => {
  if (value === 'answers') return 'objections';
  return value && VALID_KB_SECTIONS.includes(value as KbSection) ? value as KbSection : 'overview';
};

const LANDMINE_TYPE_OPTIONS = [
  { value: 'legal_risk', label: 'Legal Risk' },
  { value: 'detractor', label: 'Detractor' },
];

const LANDMINE_PRIORITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const PROHIBITED_PHRASE_SEVERITY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'legal_risk', label: 'Legal Risk' },
  { value: 'compliance_risk', label: 'Compliance Risk' },
  { value: 'brand_risk', label: 'Brand Risk' },
];

const KnowledgeBase: React.FC = () => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Guided knowledge-base setup — one URL shape, `?setup=review` (+ optional
  // `taskId`), for both "review what the AI already drafted" and "walk me
  // through setup from scratch". Which of those two an admin actually sees
  // is SeededOnboardingReview's own call (its 'fallback' phase renders the
  // conversational wizard in place when there's no usable draft to review)
  // — this page no longer needs to guess that in advance and pick between
  // two different routes for it, the way `?setup=start` vs `?setup=review`
  // used to require.
  const isGuidedSetupActive = searchParams.get('setup') === 'review';

  const clearSetupParam = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('setup');
    nextParams.delete('taskId');
    nextParams.delete('reviewSection');
    setSearchParams(nextParams);
  };

  const handleOnboardingExit = () => {
    clearSetupParam();
    // Refresh whatever the current tab already loads on mount so newly
    // captured items show up immediately without a full page reload.
    fetchItems();
    readiness.refresh();
  };

  /** Opens the perpetual guide against one known reviewable draft. */
  const openGuidedSetup = (taskId?: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('setup', 'review');
    if (taskId) nextParams.set('taskId', taskId);
    else nextParams.delete('taskId');
    setSearchParams(nextParams);
  };

  const handleRetryWebsiteTask = async (taskId: string) => {
    await retryKnowledgeGeneratorTask(taskId);
    readiness.refresh();
  };

  // Sections that share the one list panel; anything else owns kb-panel-<section>.
  const LIST_SECTIONS: KbSection[] = ['objections', 'faqs', 'landmines', 'competitors', 'playbooks'];
  const [activeSection, setActiveSectionState] = useState<KbSection>(() => {
    return sectionFromUrl(searchParams.get('tab'));
  });

  // The data tab drives which paginated list and CRUD operations are active.
  // Derive it from the selected section so a section switch takes effect in
  // the same render as the tab change.
  type KbTab = 'objections' | 'faqs' | 'landmines' | 'competitors' | 'salesplaybooks';
  const activeTab: KbTab =
    activeSection === 'objections' ? 'objections' :
    activeSection === 'faqs' ? 'faqs' :
    activeSection === 'landmines' ? 'landmines' :
    activeSection === 'competitors' ? 'competitors' :
    activeSection === 'playbooks' ? 'salesplaybooks' : 'objections';

  /** Switches section and keeps the `?tab=` query param in sync. */
  const goToSection = (section: KbSection) => {
    setActiveSectionState(section);
    const nextParams = new URLSearchParams(searchParams);
    if (section === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', section);
    setSearchParams(nextParams);
  };

  // Readiness cards are same-page links. React Router updates the query
  // string without remounting this page, so mirror that URL change back into
  // section state; otherwise the address changed while Overview stayed put.
  useEffect(() => {
    setActiveSectionState(sectionFromUrl(searchParams.get('tab')));
  }, [searchParams]);

  const handleNeedsAttentionNavigate = (section: NeedsAttentionSection, filter?: 'objections' | 'faqs') => {
    goToSection(section === 'answers' ? filter || 'objections' : section as KbSection);
    if (section === 'overview') {
      // The only overview-targeted need-attention item today is company
      // context, already visible on Overview — scroll straight to it
      // instead of leaving the admin to hunt for what changed.
      setTimeout(() => companyContextSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    }
  };

  const readiness = useKbReadiness();
  const auditSync = useKbAuditLiveSync();
  const activeAuditFindingId = searchParams.get('finding');
  const closeAuditFinding = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('finding');
    setSearchParams(nextParams);
  };
  const companyContextSectionRef = useRef<HTMLDivElement>(null);
  const importsSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeSection !== 'overview') return;
    const focusTarget = searchParams.get('focus');
    const targetRef = focusTarget === 'company-details'
      ? companyContextSectionRef
      : focusTarget === 'imports'
        ? importsSectionRef
        : null;
    if (!targetRef) return;
    window.requestAnimationFrame(() => {
      targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [activeSection, searchParams]);

  const [rescanning, setRescanning] = useState(false);
  const [rescanError, setRescanError] = useState<string | null>(null);

  // Same company info the very first scan used — re-scanning must not ask
  // the admin to re-type what Sales Guru already knows. Newest task first
  // so a company-name/URL correction made on a later scan sticks.
  const mostRecentTask = [...readiness.kgTasks].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0] || null;

  const uploadSpreadsheet = async () => {
    if (openingUploadDraft) return;
    setOpeningUploadDraft(true);
    setError(null);
    try {
      const draft = readiness.activeDraftTask || await openKnowledgeGeneratorDraft();
      navigate(`/bulk-upload?draftTaskId=${encodeURIComponent(draft._id)}`);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Could not open a knowledge draft.');
    } finally {
      setOpeningUploadDraft(false);
    }
  };

  const handleRescanWebsite = async () => {
    if (!mostRecentTask || rescanning) return;
    setRescanning(true);
    setRescanError(null);
    try {
      // Reuse the retained task when one exists so a website refresh can add
      // newly discovered suggestions without orphaning the admin's saved
      // edits in a second concurrent draft.
      const task = await rescanKnowledgeGeneratorTask(
        readiness.activeDraftTask?._id || mostRecentTask._id
      );
      readiness.refresh();
      openGuidedSetup(task._id);
    } catch (err) {
      const apiError = err as { error?: string; message?: string } | null;
      setRescanError(apiError?.error || apiError?.message || 'Could not start a new website scan.');
    } finally {
      setRescanning(false);
    }
  };

  const [landmineSubTab, setLandmineSubTab] = useState<'legal_risk' | 'detractor'>('legal_risk');
  const [items, setItems] = useState<QuestionGroup[]>([]);
  const [landmines, setLandmines] = useState<LandmineItem[]>([]);
  const [competitors, setCompetitors] = useState<CompetitorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGeneratedVariationsModalOpen, setIsGeneratedVariationsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DictionaryItem | null>(null);
  const [selectedParent, setSelectedParent] = useState<DictionaryItem | null>(null);
  const [generatedVariations, setGeneratedVariations] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: TopicCategory.OTHER as string,  // Category field now used for topic categorization
    isOriginal: true
  });
  const [landmineFormData, setLandmineFormData] = useState<Partial<LandmineItem>>({
    trigger: '',
    triggerVariations: [],
    landmineType: 'legal_risk',
    category: '',
    priority: 'medium',
    canSay: [],
    cannotSay: [],
    quickTips: '',
    isActive: true
  });
  const [editingLandmine, setEditingLandmine] = useState<LandmineItem | null>(null);
  const [isLandmineModalOpen, setIsLandmineModalOpen] = useState(false);
  const [triggerVariationInput, setTriggerVariationInput] = useState('');
  const [savingLandmine, setSavingLandmine] = useState(false);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [landmineModalError, setLandmineModalError] = useState<string | null>(null);

  // Competitor-specific state
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorItem | null>(null);
  const [isCompetitorModalOpen, setIsCompetitorModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openingUploadDraft, setOpeningUploadDraft] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [generatingFor, setGeneratingFor] = useState<string | null>(null);
  const [savingVariations, setSavingVariations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openPlaybookEditorSignal, setOpenPlaybookEditorSignal] = useState<number>();
  const [isPolishingAnswer, setIsPolishingAnswer] = useState(false);
  const [polishFeedback, setPolishFeedback] = useState<string | null>(null);
  const itemsPerPage = uiConfig.defaultPageSize;
  const inlineDialogReturnFocusRef = useRef<HTMLElement | null>(null);
  const hasOpenInlineDialog = isModalOpen || isLandmineModalOpen || isGeneratedVariationsModalOpen;

  const closeInlineDialogs = React.useCallback(() => {
    setIsModalOpen(false);
    setIsLandmineModalOpen(false);
    setIsGeneratedVariationsModalOpen(false);
    setGeneratedVariations([]);
    setLandmineModalError(null);
  }, []);

  useEffect(() => {
    if (!hasOpenInlineDialog) return undefined;

    inlineDialogReturnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusFrame = window.requestAnimationFrame(() => {
      const dialog = document.querySelector<HTMLElement>('[data-kb-modal="true"]');
      // A real field wins over a button when both are present — e.g. the
      // Objection/FAQ type toggle now sits above the Question field, and a
      // decorative/selection button shouldn't steal initial focus from it.
      const initialFocus = dialog?.querySelector<HTMLElement>('textarea, input, select')
        || dialog?.querySelector<HTMLElement>('button');
      initialFocus?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeInlineDialogs();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      inlineDialogReturnFocusRef.current?.focus();
      inlineDialogReturnFocusRef.current = null;
    };
  }, [closeInlineDialogs, hasOpenInlineDialog]);

  // Search state
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<QuestionGroup[]>([]);
  const [searchPagination, setSearchPagination] = useState<PaginationInfo | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);

  // Multi-select state
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [selectedLandmineIds, setSelectedLandmineIds] = useState<Set<string>>(new Set());
  const [selectedCompetitorIds, setSelectedCompetitorIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Selection handlers for objections/FAQs
  const handleToggleItem = (itemId: string, item: QuestionGroup) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        // Deselecting original - also deselect all variations
        newSet.delete(itemId);
        item.variations?.forEach(v => newSet.delete(v._id));
      } else {
        // Selecting original - also select all variations
        newSet.add(itemId);
        item.variations?.forEach(v => newSet.add(v._id));
      }
      return newSet;
    });
  };

  const handleToggleVariation = (variationId: string) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const handleToggleLandmine = (landmineId: string) => {
    setSelectedLandmineIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(landmineId)) {
        newSet.delete(landmineId);
      } else {
        newSet.add(landmineId);
      }
      return newSet;
    });
  };

  // selectedItemIds mixes two different kinds of thing: a whole objection/FAQ
  // (selecting one auto-selects all of its variations, so "6 items" in the
  // raw set can really mean "1 objection, which happens to carry 5
  // phrasings") and a standalone alternate phrasing selected on its own,
  // without its parent. Reporting a flat "N items" count conflates those —
  // an admin who ticked one objection sees a number that implies they picked
  // six separate things. This mirrors handleBulkDelete's own originals/
  // variations split below so the label never disagrees with what's
  // actually deleted.
  const describeObjectionFaqSelection = (): string => {
    let originals = 0;
    let phrasings = 0;
    items.forEach(item => {
      if (selectedItemIds.has(item._id)) {
        originals += 1;
      } else {
        item.variations?.forEach(variation => {
          if (selectedItemIds.has(variation._id)) phrasings += 1;
        });
      }
    });
    const noun = activeTab === 'objections' ? 'objection' : 'FAQ';
    const parts: string[] = [];
    if (originals > 0) parts.push(`${originals} ${noun}${originals !== 1 ? 's' : ''}`);
    if (phrasings > 0) parts.push(`${phrasings} alternate phrasing${phrasings !== 1 ? 's' : ''}`);
    return parts.join(' and ') || '0 items';
  };

  const handleBulkDelete = async () => {
    const count = activeTab === 'landmines' ? selectedLandmineIds.size :
                  activeTab === 'competitors' ? selectedCompetitorIds.size :
                  selectedItemIds.size;
    const selectionLabel = activeTab === 'landmines' ? `${count} landmine${count !== 1 ? 's' : ''}` :
                            activeTab === 'competitors' ? `${count} competitor${count !== 1 ? 's' : ''}` :
                            describeObjectionFaqSelection();
    if (!(await confirm({
      title: `Delete ${selectionLabel}?`,
      message: 'This action cannot be undone.',
      confirmLabel: `Delete ${selectionLabel}`,
      variant: 'destructive',
    }))) {
      return;
    }

    setBulkDeleting(true);
    try {
      if (activeTab === 'landmines') {
        // Delete landmines
        for (const id of selectedLandmineIds) {
          await deleteLandmine(id);
        }
        setSelectedLandmineIds(new Set());
        setSuccess(`Successfully deleted ${count} landmine${count !== 1 ? 's' : ''}`);
      } else if (activeTab === 'competitors') {
        // Delete competitors
        for (const id of selectedCompetitorIds) {
          await deleteCompetitor(id);
        }
        setSelectedCompetitorIds(new Set());
        setSuccess(`Successfully deleted ${count} competitor${count !== 1 ? 's' : ''}`);
      } else {
        // Delete objections/FAQs
        // Separate originals and variations
        const originalIdsToDelete = new Set<string>();
        const variationIdsToDelete = new Set<string>();

        items.forEach(item => {
          if (selectedItemIds.has(item._id)) {
            // This is an original item that's selected
            originalIdsToDelete.add(item._id);
          } else {
            // Check if any variations are selected
            item.variations?.forEach(variation => {
              if (selectedItemIds.has(variation._id)) {
                variationIdsToDelete.add(variation._id);
              }
            });
          }
        });


        // Delete originals first (backend will cascade delete their variations)
        for (const id of originalIdsToDelete) {
          try {
            if (activeTab === 'objections') {
              await deleteObjection(id);
            } else {
              await deleteFAQ(id);
            }
          } catch (err) {
            console.error(`Failed to delete item ${id}:`, err);
          }
        }

        // Delete standalone variations (variations whose parent wasn't deleted)
        for (const id of variationIdsToDelete) {
          try {
            if (activeTab === 'objections') {
              await deleteObjection(id);
            } else {
              await deleteFAQ(id);
            }
          } catch (err) {
            console.error(`Failed to delete variation ${id}:`, err);
          }
        }

        setSelectedItemIds(new Set());

        const noun = activeTab === 'objections' ? 'objection' : 'FAQ';
        if (originalIdsToDelete.size > 0 && variationIdsToDelete.size > 0) {
          setSuccess(`Successfully deleted ${originalIdsToDelete.size} ${noun}${originalIdsToDelete.size !== 1 ? 's' : ''} and ${variationIdsToDelete.size} alternate phrasing${variationIdsToDelete.size !== 1 ? 's' : ''}`);
        } else if (originalIdsToDelete.size > 0) {
          setSuccess(`Successfully deleted ${originalIdsToDelete.size} ${noun}${originalIdsToDelete.size !== 1 ? 's' : ''} and their alternate phrasings`);
        } else if (variationIdsToDelete.size > 0) {
          setSuccess(`Successfully deleted ${variationIdsToDelete.size} alternate phrasing${variationIdsToDelete.size !== 1 ? 's' : ''}`);
        }
      }
      fetchItems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete some items');
      console.error(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Clear selections when tab changes
  useEffect(() => {
    setSelectedItemIds(new Set());
    setSelectedLandmineIds(new Set());
    setSelectedCompetitorIds(new Set());
    // Reset search when tab changes
    setSearchTerm('');
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchPagination(null);
    setSearchCurrentPage(1);
  }, [activeTab]);

  // Reset to first page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Fetch data when component mounts, tab changes, or page changes.
  // Overview renders entirely separate content (see useKbReadiness /
  // ImportsAndDrafts, folded into Overview), so it needs no paginated fetch.
  useEffect(() => {
    if (activeSection === 'overview') return;

    let isSubscribed = true;

    const fetchData = async () => {
      if (!isSubscribed) return;

      setLoading(true);
      setError(null);
      try {
        if (activeTab === 'objections') {
          const response = await getObjectionsGrouped(currentPage, itemsPerPage);
          if (isSubscribed) {
            const groupedItems = (response?.objections || []).map(item => ({
              ...item,
              isExpanded: false
            }));
            setItems(groupedItems);
            setPagination(response?.pagination || null);
          }
        } else if (activeTab === 'faqs') {
          const response = await getFAQsGrouped(currentPage, itemsPerPage);
          if (isSubscribed) {
            const groupedItems = (response?.faqs || []).map(item => ({
              ...item,
              isExpanded: false
            }));
            setItems(groupedItems);
            setPagination(response?.pagination || null);
          }
        } else if (activeTab === 'landmines') {
          const response = await getLandmines(currentPage, itemsPerPage);
          if (isSubscribed) {
            // Sort landmines alphabetically by trigger
            const sortedLandmines = (response.landmines || []).sort((a, b) =>
              a.trigger.localeCompare(b.trigger)
            );
            setLandmines(sortedLandmines);
            setPagination(response.pagination || null);
          }
        } else if (activeTab === 'competitors') {
          const response = await getCompetitors(currentPage, itemsPerPage);
          if (isSubscribed) {
            // Sort competitors alphabetically by name (trigger field)
            const sortedCompetitors = (response?.competitors || []).sort((a, b) =>
              a.name.localeCompare(b.name)
            );
            setCompetitors(sortedCompetitors);
            setPagination(response?.pagination || null);
          }
        }
      } catch (err) {
        if (isSubscribed) {
          setError('Failed to fetch dictionary items');
          console.error(err);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to prevent state updates if component unmounts or effect re-runs
    return () => {
      isSubscribed = false;
    };
  }, [activeTab, currentPage, itemsPerPage, activeSection]);

  // Clear search term when switching to tabs without search
  useEffect(() => {
    if (activeTab === 'landmines' || activeTab === 'competitors') {
      setSearchTerm('');
    }
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'objections') {
        const response = await getObjectionsGrouped(currentPage, itemsPerPage);
        const groupedItems = (response?.objections || []).map(item => ({
          ...item,
          isExpanded: false
        }));
        setItems(groupedItems);
        setPagination(response?.pagination || null);
      } else if (activeTab === 'faqs') {
        const response = await getFAQsGrouped(currentPage, itemsPerPage);
        const groupedItems = (response?.faqs || []).map(item => ({
          ...item,
          isExpanded: false
        }));
        setItems(groupedItems);
        setPagination(response?.pagination || null);
      } else if (activeTab === 'landmines') {
        const response = await getLandmines(currentPage, itemsPerPage);
        // Sort landmines alphabetically by trigger
        const sortedLandmines = (response.landmines || []).sort((a, b) =>
          a.trigger.localeCompare(b.trigger)
        );
        setLandmines(sortedLandmines);
        setPagination(response.pagination || null);
      } else if (activeTab === 'competitors') {
        const response = await getCompetitors(currentPage, itemsPerPage);
        // Sort competitors alphabetically by name
        const sortedCompetitors = (response?.competitors || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setCompetitors(sortedCompetitors);
        setPagination(response?.pagination || null);
      }
    } catch (err) {
      setError('Failed to fetch dictionary items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (itemId: string) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item._id === itemId
          ? { ...item, isExpanded: !item.isExpanded }
          : item
      )
    );
  };

  // Handle search with debouncing
  const handleSearch = async (query: string, page: number = 1) => {
    // Search is available only for objection and FAQ lists.
    if (activeTab !== 'objections' && activeTab !== 'faqs') {
      return;
    }

    if (!query.trim() || query.trim().length < 2) {
      // Empty or too-short search - exit search mode
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchPagination(null);
      setSearchCurrentPage(1);
      return;
    }

    setSearchLoading(true);
    setError(null);

    try {
      const type = activeTab === 'objections' ? 'objection' : 'faq';
      const response = await searchDictionary(query, type, page, itemsPerPage);

      if (response.success) {
        // Add isExpanded property to search results
        const resultsWithExpanded = response.results.map(item => ({
          ...item,
          isExpanded: false
        }));
        setSearchResults(resultsWithExpanded);
        setSearchPagination(response.pagination);
        setSearchCurrentPage(page);
        setIsSearchMode(true);
      } else {
        setError(response.error || 'Search failed');
        setIsSearchMode(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to search');
      setIsSearchMode(false);
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Only set up debouncing for objections and FAQs
    if (activeTab !== 'objections' && activeTab !== 'faqs') {
      return;
    }
    const debounceTimer = setTimeout(() => {
      handleSearch(searchTerm, 1);
    }, 300);

    return () => clearTimeout(debounceTimer);
    // handleSearch is a plain function rebuilt on every render; listing it would restart the
    // 300ms debounce. The callback reads searchTerm and activeTab, both deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeTab]);

  const openLandmineEditor = () => {
    setEditingLandmine(null);
    setLandmineFormData({
      trigger: '',
      triggerVariations: [],
      landmineType: 'legal_risk',
      category: '',
      priority: 'medium',
      canSay: [],
      cannotSay: [],
      quickTips: '',
      isActive: true
    });
    setTriggerVariationInput('');
    setLandmineModalError(null);
    setIsLandmineModalOpen(true);
  };

  const openCompetitorEditor = () => {
    setEditingCompetitor(null);
    setIsCompetitorModalOpen(true);
  };

  const openAnswerEditor = () => {
    setPolishFeedback(null);
    setEditingItem(null);
    setFormData({
      question: '',
      answer: '',
      category: TopicCategory.OTHER as string,
      isOriginal: true
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    if (activeTab === 'landmines') {
      openLandmineEditor();
    } else if (activeTab === 'competitors') {
      openCompetitorEditor();
    } else {
      openAnswerEditor();
    }
  };

  const handleAddContent = (contentType: AddContentType) => {
    if (contentType === 'objection' || contentType === 'faq') {
      goToSection(contentType === 'faq' ? 'faqs' : 'objections');
      openAnswerEditor();
      return;
    }
    if (contentType === 'landmine') {
      goToSection('landmines');
      openLandmineEditor();
      return;
    }
    if (contentType === 'competitor') {
      goToSection('competitors');
      openCompetitorEditor();
      return;
    }
    if (contentType === 'playbook') {
      goToSection('playbooks');
      setOpenPlaybookEditorSignal((signal) => (signal || 0) + 1);
    }
  };

  const handleEdit = (item: DictionaryItem) => {
    setPolishFeedback(null);
    setEditingItem(item);
    setFormData({
      question: item.question,
      answer: item.answer,
      category: item.category || TopicCategory.OTHER as string,  // Use existing category or default to OTHER
      isOriginal: item.isOriginal || false
    });
    setIsModalOpen(true);
  };

  // One entry point for a new phrasing, written by hand or drafted by AI —
  // both land in the same list-of-rows modal (below), starting with a
  // single blank row so it's immediately usable without an AI call.
  const handleAddVariation = (parent: DictionaryItem) => {
    setSelectedParent(parent);
    setGeneratedVariations(['']);
    setIsGeneratedVariationsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (editingItem) {
        // Update existing item
        if (activeTab === 'objections') {
          await updateObjection(editingItem._id, formData);
        } else {
          await updateFAQ(editingItem._id, formData);
        }
        setSuccess('Item updated successfully. Embeddings will be regenerated automatically.');
      } else {
        // Add new item
        let result: any;
        const itemType = activeTab === 'objections' ? 'objection' : 'FAQ';

        if (activeTab === 'objections') {
          result = await addObjection(formData.question, formData.answer, formData.category, formData.isOriginal);
        } else {
          result = await addFAQ(formData.question, formData.answer, formData.category);
        }

        if (result?.variationCount > 0) {
          setSuccess(`Created the ${itemType} along with ${result.variationCount} alternate phrasings.`);
        } else {
          setSuccess(`Created the ${itemType}. Add alternate phrasings any time to help Apexia Labs recognize it more often.`);
        }
      }

      setIsSubmitting(false);
      setIsModalOpen(false);

      // For new items, reset to page 1 and fetch immediately
      if (!editingItem) {
        // If we're not on page 1, reset to it
        if (currentPage !== 1) {
          setCurrentPage(1);  // This will trigger useEffect
        } else {
          // If already on page 1, manually fetch to see the new item
          fetchItems();
        }
      } else {
        // For edits, just refresh current page
        fetchItems();
      }

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setIsSubmitting(false);
      setError('Failed to save item');
      console.error(err);
    }
  };

  const handlePolishAnswer = async () => {
    const fields: KnowledgePolishField[] = [{
      id: 'answer',
      text: formData.answer,
      contentRole: 'agent-talk-track' as const,
    }];
    if (!formData.answer.trim()) {
      setPolishFeedback('Enter an answer before polishing.');
      return;
    }

    setIsPolishingAnswer(true);
    setPolishFeedback(null);
    try {
      const polished = await polishKnowledgeFields(activeTab === 'faqs' ? 'faq' : 'objection', fields);
      const byId = new Map(polished.map((field) => [field.id, field.text]));
      setFormData((current) => ({
        ...current,
        answer: byId.get('answer') || current.answer,
      }));
      setPolishFeedback(
        polished.some((field) => field.changed)
          ? 'Polished wording applied. Review it before saving.'
          : 'This wording is already ready for a live call.'
      );
    } catch (polishError) {
      console.error('Error polishing knowledge:', polishError);
      setPolishFeedback('Could not polish this content. Your original wording is unchanged.');
    } finally {
      setIsPolishingAnswer(false);
    }
  };

  // Appends AI-drafted phrasings to whatever's already in the modal, rather
  // than replacing it — dropping only the still-blank placeholder row, so a
  // phrasing the admin already typed by hand is never discarded by asking
  // for AI suggestions too.
  const handleGenerateVariations = async (item: DictionaryItem) => {
    setError(null);
    setGeneratingFor(item._id);

    try {
      const result: any = activeTab === 'objections'
        ? await generateObjectionVariations(item._id, 3)
        : await generateFAQVariations(item._id, 3);

      if (result && result.variations) {
        setSelectedParent(item);
        // Clean up variations - remove surrounding quotes if present
        const cleanedVariations = result.variations.map((v: string) => {
          // Remove surrounding double quotes if the entire string is wrapped in them
          if (v.startsWith('"') && v.endsWith('"')) {
            return v.slice(1, -1);
          }
          return v;
        });
        setGeneratedVariations((prev) => [...prev.filter((v) => v.trim()), ...cleanedVariations]);
        setIsGeneratedVariationsModalOpen(true);
      } else {
        setError('Failed to generate variations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate variations');
      console.error(err);
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleEditLandmine = (landmine: LandmineItem) => {
    setEditingLandmine(landmine);
    setLandmineFormData(landmine);
    setTriggerVariationInput('');
    setLandmineModalError(null);
    setIsLandmineModalOpen(true);
  };

  const handleDeleteLandmine = async (id: string, trigger: string) => {
    if (!(await confirm({
      title: `Delete the landmine "${trigger}"?`,
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete landmine',
      variant: 'destructive',
    }))) {
      return;
    }
    try {
      await deleteLandmine(id);
      setSuccess('Landmine deleted successfully');
      fetchItems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete landmine');
      console.error(err);
    }
  };

  const handleToggleLandmineStatus = async (landmine: LandmineItem) => {
    try {
      await toggleLandmineStatus(landmine._id);
      setSuccess(`"${landmine.trigger}" status updated`);
      fetchItems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to toggle landmine status');
      console.error(err);
    }
  };

  const handleApproveLandmine = async (landmine: LandmineItem) => {
    try {
      await reviewLandmine(landmine._id, landmineDraftVersion(landmine));
      setSuccess(`"${landmine.trigger}" approved for live calls`);
      fetchItems();
      readiness.refresh();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      if (err?.code === 'LANDMINE_CHANGED_DURING_REVIEW') {
        await fetchItems();
        setError('This guidance changed while you were reviewing it. The latest version has been reloaded.');
      } else {
        setError('Failed to approve guidance');
      }
      console.error(err);
    }
  };

  const handleAddTriggerVariation = () => {
    const variation = triggerVariationInput.trim();
    if (variation && !(landmineFormData.triggerVariations || []).includes(variation)) {
      setLandmineFormData({
        ...landmineFormData,
        triggerVariations: [...(landmineFormData.triggerVariations || []), variation]
      });
      setTriggerVariationInput('');
    }
  };

  const handleDeleteTriggerVariation = (variation: string) => {
    setLandmineFormData({
      ...landmineFormData,
      triggerVariations: (landmineFormData.triggerVariations || []).filter(v => v !== variation)
    });
  };

  const handleGenerateTriggerVariations = async () => {
    const trigger = landmineFormData.trigger?.trim();
    if (!trigger) {
      setLandmineModalError('Please enter a trigger word/phrase first');
      return;
    }

    setGeneratingVariations(true);
    setLandmineModalError(null);

    try {
      const variations = await generateLandmineVariations(trigger);
      if (variations && variations.length > 0) {
        // Add generated variations to existing ones, avoiding duplicates
        const existingVariations = landmineFormData.triggerVariations || [];
        const { merged, addedCount } = mergeGeneratedVariations(existingVariations, variations, trigger);
        setLandmineFormData({
          ...landmineFormData,
          triggerVariations: merged
        });
        setSuccess(`Generated ${addedCount} variation${addedCount !== 1 ? 's' : ''}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setLandmineModalError('No variations could be generated. Try a different trigger phrase.');
      }
    } catch (err) {
      console.error('Error generating variations:', err);
      setLandmineModalError('Failed to generate variations. Please try again.');
    } finally {
      setGeneratingVariations(false);
    }
  };

  const handleLandmineSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLandmineModalError(null);

    // Validation
    if (!landmineFormData.trigger?.trim()) {
      setLandmineModalError('Trigger word/phrase is required');
      return;
    }

    // Category is only required for legal_risk type
    if (landmineFormData.landmineType === 'legal_risk' && !landmineFormData.category?.trim()) {
      setLandmineModalError('Category is required for Legal Risk landmines');
      return;
    }

    try {
      setSavingLandmine(true);
      // Filter out empty fields before submitting
      const processedData = {
        ...landmineFormData,
        triggerVariations: (landmineFormData.triggerVariations || []).filter(v => v.trim()),
        // Only include canSay/cannotSay for legal_risk type
        canSay: landmineFormData.landmineType === 'legal_risk'
          ? (landmineFormData.canSay || []).filter(item => item.point.trim())
          : [],
        cannotSay: landmineFormData.landmineType === 'legal_risk'
          ? (landmineFormData.cannotSay || []).filter(item => item.point.trim())
          : [],
        // Clear category for detractor type
        category: landmineFormData.landmineType === 'detractor' ? undefined : landmineFormData.category
      };

      if (editingLandmine) {
        await updateLandmine(editingLandmine._id, processedData);
        setSuccess('Landmine updated successfully');
      } else {
        await addLandmine(processedData);
        setSuccess('Landmine added successfully');
      }

      setIsLandmineModalOpen(false);
      setLandmineModalError(null);
      fetchItems();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error saving landmine:', err);

      // Handle duplicate key error
      const errorText = err?.error || err?.message || '';
      if (isDuplicateKeyError(errorText)) {
        setLandmineModalError('A landmine with this trigger already exists. Please use a different trigger word.');
      } else {
        setLandmineModalError(err?.message || err?.error || 'Failed to save landmine. Please try again.');
      }
    } finally {
      setSavingLandmine(false);
    }
  };

  const handleSaveGeneratedVariations = async () => {
    if (!selectedParent || savingVariations) return;

    setSavingVariations(true);
    setError(null);
    let savedCount = 0;

    try {
      for (const variation of generatedVariations) {
        if (variation.trim()) {
          try {
            if (activeTab === 'objections') {
              await addObjectionVariation(selectedParent._id, variation);
            } else {
              await addFAQVariation(selectedParent._id, variation);
            }
            savedCount++;
          } catch (err) {
            console.error('Error saving variation:', err);
          }
        }
      }

      if (savedCount > 0) {
        setSuccess(`Successfully saved ${savedCount} variation${savedCount !== 1 ? 's' : ''}`);
        setIsGeneratedVariationsModalOpen(false);
        setGeneratedVariations([]);
        fetchItems();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError('No variations were saved');
      }
    } catch (err) {
      setError('Failed to save variations');
      console.error(err);
    } finally {
      setSavingVariations(false);
    }
  };

  // Determine which items to display
  // For objections and FAQs: use search results if in search mode, otherwise use regular items
  const displayItems = (activeTab === 'objections' || activeTab === 'faqs')
    ? (isSearchMode ? searchResults : items)
    : items;

  if (isGuidedSetupActive) {
    return (
      <MainLayout title="Knowledge Base Management" hideSidebar>
        <div className="apexia-kb apexia-review">
          <SeededOnboardingReview
            onExit={handleOnboardingExit}
            taskId={searchParams.get('taskId') || undefined}
            fallbackDraftTaskId={readiness.activeDraftTask?._id}
            initialReviewSection={(() => {
              const value = searchParams.get('reviewSection');
              return isOnboardingReviewSection(value) ? value : undefined;
            })()}
          />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Knowledge Base Management">
      <div className="apexia-kb apexia-kb-page">
        <PageHeader
          title="Knowledge Base"
          description="Review and manage the information your sales assistant uses during live calls."
          actions={(
            <>
              {activeSection === 'overview' && <AddContentMenu onSelect={handleAddContent} />}
              {/* Contextual — only the action that matters for the section
                  you're on, not a row of unrelated tools. */}
              {(activeSection === 'objections' || activeSection === 'faqs') && (
                <>
                  <button
                    onClick={handleAdd}
                    className="kb-button kb-button-primary px-4 py-2 text-sm"
                  >
                    <PlusIcon style={{ fontSize: '1.1rem' }} />
                    {activeSection === 'objections' ? 'Add Objection' : 'Add FAQ'}
                  </button>
                  <button
                    onClick={uploadSpreadsheet}
                    disabled={openingUploadDraft}
                    className="kb-button kb-button-secondary px-4 py-2 text-sm"
                    title="Add objections or FAQs from a spreadsheet"
                  >
                    <UploadIcon style={{ fontSize: '1.1rem' }} />
                    {openingUploadDraft ? 'Opening draft…' : 'Upload spreadsheet'}
                  </button>
                </>
              )}
              {activeSection === 'landmines' && (
                <button
                  onClick={handleAdd}
                  className="kb-button kb-button-primary px-4 py-2 text-sm"
                >
                  <PlusIcon style={{ fontSize: '1.1rem' }} />
                  Add Landmine
                </button>
              )}
              {activeSection === 'competitors' && (
                <button
                  onClick={handleAdd}
                  className="kb-button kb-button-primary px-4 py-2 text-sm"
                >
                  <PlusIcon style={{ fontSize: '1.1rem' }} />
                  Add Competitor
                </button>
              )}
            </>
          )}
        />

      {/* Success/Error Messages */}
      {auditSync.state && (
        <KbAuditLiveSyncNotice state={auditSync.state} onCheck={auditSync.checkNow} onDismiss={auditSync.dismiss} />
      )}
      {success && (
        <div role="status" aria-live="polite" className="mb-4 p-4 bg-success-tint border border-success rounded-lg flex items-center">
          <CheckCircleIcon className="text-success mr-2" style={{ fontSize: '1.25rem' }} />
          <span className="text-ink">{success}</span>
        </div>
      )}

      {error && (
        <div role="alert" className="mb-4 p-4 bg-error-tint border border-error rounded-lg flex items-center">
          <XCircleIcon className="text-error mr-2" style={{ fontSize: '1.25rem' }} />
          <span className="text-ink">{error}</span>
        </div>
      )}

      {/* Sections */}
      <Tabs
        idPrefix="kb"
        ariaLabel="Knowledge Base sections"
        tutorialId="knowledge-tabs"
        value={activeSection}
        onChange={(id) => goToSection(id as KbSection)}
        items={([
            ['overview', 'Overview', 'tab-overview'],
            ['objections', 'Objections', 'tab-objections'],
            ['faqs', 'FAQs', 'tab-faqs'],
            ['landmines', 'Landmines', 'tab-landmines'],
            ['competitors', 'Competitors', 'tab-competitors'],
            ['playbooks', 'Playbooks', 'tab-salesplaybooks'],
          ] as [KbSection, string, string][]).map(([section, label, tutorialId]) => ({
            id: section,
            label,
            tutorialId,
            disabled: loading,
            panelId: LIST_SECTIONS.includes(section) ? 'kb-panel-list' : `kb-panel-${section}`,
          }))}
      />

      {/* Overview — readiness, what needs attention, a quick test box, and
          company context. This is the calm landing page for returning
          admins; the raw editing tables live one click away in the other
          sections. */}
      {activeSection === 'overview' && (
        <div className="kb-overview" role="tabpanel" id="kb-panel-overview" aria-labelledby="kb-tab-overview" tabIndex={0}>
          <section className="kb-overview-section kb-overview-readiness" aria-labelledby="kb-coverage-heading">
            <KnowledgeSectionHeading
              id="kb-coverage-heading"
              title="Coverage at a glance"
              description="See what is ready for live calls and where your knowledge still needs work."
            />
            <ReadinessStrip cards={readiness.cards} loading={readiness.loading} />
          </section>

          <div className="kb-overview-split">
            <section className="kb-overview-section" aria-labelledby="kb-attention-heading">
              <KnowledgeSectionHeading
                id="kb-attention-heading"
                title={readiness.needsAttention.length > 0 ? 'Needs your attention' : 'Everything is ready'}
                description={readiness.needsAttention.length > 0
                  ? 'The highest-impact items to review next.'
                  : 'No knowledge base items need your attention.'}
              />
              <NeedsAttentionQueue
                items={readiness.needsAttention}
                loading={readiness.loading}
                onNavigate={handleNeedsAttentionNavigate}
              />
            </section>
            <section className="kb-overview-section kb-overview-test" aria-label="Test published knowledge">
              <QuickTestBox liveAnswerCount={readiness.liveAnswerCount} />
            </section>
          </div>

          <section ref={companyContextSectionRef} className="kb-overview-section kb-overview-company" aria-labelledby="kb-company-heading">
            <KnowledgeSectionHeading
              id="kb-company-heading"
              title="Company details"
              description="The shared business context used by guidance, training, and generated drafts."
            />
            {readiness.hasDraftCompanyContext && !readiness.hasLiveCompanyContext ? (
              <div className="rounded-lg bg-info-tint border border-info p-4">
                <div>
                  <p className="text-sm font-medium text-ink">Already included in your current draft</p>
                  <p className="text-xs text-ink-secondary mt-1">
                    Your company details will be reviewed with the rest of this draft. You do not need to enter them again here.
                  </p>
                  {readiness.reviewableTask && <button type="button" onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('setup', 'review');
                    params.set('taskId', readiness.reviewableTask!._id);
                    params.set('reviewSection', 'company_context');
                    setSearchParams(params);
                  }} className="mt-3 min-h-[44px] rounded-lg border border-signature bg-surface text-signature hover:bg-signature-tint px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-signature">Review company details in draft</button>}
                </div>
              </div>
            ) : (
              <CompanyContextCard />
            )}
          </section>

          <div ref={importsSectionRef} className="kb-overview-section kb-overview-imports">
            <ImportsAndDrafts
              kgTasks={readiness.kgTasks}
              activeDraftTask={readiness.activeDraftTask}
              onOpenDraft={openGuidedSetup}
              onRetryWebsiteTask={handleRetryWebsiteTask}
              onScanWebsite={mostRecentTask ? handleRescanWebsite : undefined}
              websiteScanPending={rescanning}
              websiteScanError={rescanError}
              onImported={readiness.refresh}
            />
          </div>
        </div>
      )}

      {activeAuditFindingId && readiness.auditFindings.length > 0 && (
        <KbAuditReviewPanel
          findings={readiness.auditFindings}
          activeFindingId={activeAuditFindingId}
          onClose={closeAuditFinding}
          onApplied={(findingIds) => {
            auditSync.begin(findingIds);
            readiness.refresh();
            closeAuditFinding();
          }}
        />
      )}

      {/* Filters and Actions - only for the sections with a paginated list */}
      {(activeSection === 'objections' || activeSection === 'faqs' || activeSection === 'landmines' || activeSection === 'competitors') && (
        <div className="mb-4 flex flex-col sm:flex-row items-start gap-4">
          {/* Only show search for objections and FAQs */}
          {(activeTab === 'objections' || activeTab === 'faqs') ? (
            <div className="flex-1 w-full sm:w-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" style={{ fontSize: '1.25rem' }} />
                <input
                  type="text"
                  aria-label="Search published questions and answers"
                  placeholder="Search questions and answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10 py-2 w-full border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                />
                {searchTerm && (
                  <button
                    type="button"
                    aria-label="Clear published answer search"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary"
                    title="Clear search"
                  >
                    <CloseIcon style={{ fontSize: '1.25rem' }} />
                  </button>
                )}
              </div>
              {isSearchMode && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-surface-sunken text-ink-secondary rounded-full">
                    <MagnifyingGlassIcon style={{ fontSize: '0.875rem' }} className="mr-1" />
                    Search Results
                  </span>
                  <span className="text-xs text-ink-secondary">
                    {searchPagination?.total || 0} result{(searchPagination?.total || 0) !== 1 ? 's' : ''} found
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1"></div>
          )}

          <div className="flex flex-wrap gap-4 shrink-0">

          {/* Bulk Delete Button - Show when items are selected */}
          {((activeTab === 'objections' || activeTab === 'faqs') && selectedItemIds.size > 0) ||
           (activeTab === 'landmines' && selectedLandmineIds.size > 0) ||
           (activeTab === 'competitors' && selectedCompetitorIds.size > 0) ? (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 ${
                bulkDeleting
                  ? 'bg-error opacity-60 cursor-not-allowed'
                  : 'bg-error hover:opacity-90'
              }`}
            >
              {bulkDeleting ? (
                <ArrowPathIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
              ) : (
                <TrashIcon style={{ fontSize: '1.25rem' }} />
              )}
              {bulkDeleting ? 'Deleting...' : `Delete ${
                activeTab === 'landmines' ? `${selectedLandmineIds.size} landmine${selectedLandmineIds.size !== 1 ? 's' : ''}` :
                activeTab === 'competitors' ? `${selectedCompetitorIds.size} competitor${selectedCompetitorIds.size !== 1 ? 's' : ''}` :
                describeObjectionFaqSelection()
              }`}
            </button>
          ) : null}

          <button
            onClick={fetchItems}
            className="px-4 py-2 bg-surface-subtle text-ink-secondary rounded-lg hover:bg-surface-sunken flex items-center gap-2"
          >
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1.25rem' }} />
            Refresh
          </button>
          {/* The "Add" action itself lives in the page header now, so it's
              one clear place per context rather than duplicated here. */}
          </div>
        </div>
      )}

      {/* Items List — scoped to every section that renders something in this
          block, unlike the narrower Filters and Actions guard above (which
          rightly excludes Playbooks — it has no search/filter chips). This
          block also carries the activeTab === 'salesplaybooks' branch that
          renders <SalesPlaybookManagement/>, so 'playbooks' must stay in
          this list too — leaving it out blanks that whole tab. */}
      {(activeSection === 'objections' || activeSection === 'faqs' || activeSection === 'landmines' || activeSection === 'competitors' || activeSection === 'playbooks') && (
      <div
        className="kb-content-panel bg-surface overflow-hidden"
        role="tabpanel"
        id="kb-panel-list"
        aria-labelledby={`kb-tab-${activeSection}`}
        tabIndex={0}
      >
        {(loading || searchLoading) ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="animate-spin mx-auto text-ink-muted" style={{ fontSize: '2rem' }} />
            <p className="mt-2 text-ink-muted">{searchLoading ? 'Searching...' : 'Loading...'}</p>
          </div>
        ) : activeTab === 'landmines' ? (
          // Compliance display with sub-tabs — same legal_risk/detractor
          // landmine data underneath, friendlier labels on top.
          <div>
            <div className="px-4 pt-4 pb-2 bg-warning-tint border-b border-warning">
              <p className="text-sm text-ink">
                This guidance can appear during live calls. Review carefully before activating.
              </p>
            </div>
            {/* Sub-tabs for Legal Risk and Detractor */}
            <div className="border-b border-hairline bg-surface-subtle px-4">
              <nav className="kb-section-subtabs flex gap-1 py-1">
                <button
                  onClick={() => setLandmineSubTab('legal_risk')}
                  className={`py-2 px-3 font-medium text-sm ${landmineSubTab === 'legal_risk' ? 'is-active' : ''}`}
                >
                  Legal Risk ({landmines.filter(l => l.landmineType !== 'detractor').length})
                </button>
                {landmines.some((landmine) => landmine.landmineType === 'detractor') && (
                  <button
                    onClick={() => setLandmineSubTab('detractor')}
                    className={`py-2 px-3 font-medium text-sm ${landmineSubTab === 'detractor' ? 'is-active' : ''}`}
                  >
                    Detractors ({landmines.filter(l => l.landmineType === 'detractor').length})
                  </button>
                )}
              </nav>
            </div>

            {/* Filtered landmines list */}
            {landmines.filter(l => landmineSubTab === 'detractor' ? l.landmineType === 'detractor' : l.landmineType !== 'detractor').length === 0 ? (
              <div className="p-8 text-center text-ink-muted">
                {landmineSubTab === 'detractor'
                  ? "No conversation habits added yet. Add phrases reps should avoid and what to say instead."
                  : 'No landmines added yet. Add phrases that need careful handling on a call.'}
              </div>
            ) : (
              <div className="divide-y divide-hairline">
                {landmines
                  .filter(l => landmineSubTab === 'detractor' ? l.landmineType === 'detractor' : l.landmineType !== 'detractor')
                  .map((landmine) => (
                  <div key={landmine._id} className="p-4 hover:bg-surface-subtle transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 mr-4">
                        {/* Checkbox for selection */}
                        <input
                          type="checkbox"
                          checked={selectedLandmineIds.has(landmine._id)}
                          onChange={() => handleToggleLandmine(landmine._id)}
                          className="mt-1 h-4 w-4 text-signature rounded border-ink-border focus:ring-signature"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-sm font-medium text-ink">
                              {landmine.trigger}
                            </h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              landmine.priority === 'high' ? 'bg-error-tint text-error' :
                              landmine.priority === 'medium' ? 'bg-warning-tint text-warning' :
                              'bg-surface-sunken text-ink-secondary'
                            }`}>
                              {landmine.priority}
                            </span>
                            {landmine.landmineType !== 'detractor' && landmine.category && (
                              <span className="px-2 py-1 text-xs bg-surface-sunken text-ink-secondary rounded-full">
                                {landmine.category}
                              </span>
                            )}
                            {landmine.approvalStatus === 'pending' && (
                              <span className="px-2 py-1 text-xs bg-warning-tint text-warning rounded-full">
                                Waiting for approval
                              </span>
                            )}
                            {landmine.isActive ? (
                              <CheckCircleIcon className="text-success" style={{ fontSize: '1rem' }} titleAccess="Active — available on live calls" />
                            ) : (
                              <XCircleIcon className="text-ink-muted" style={{ fontSize: '1rem' }} titleAccess="Inactive — hidden from live calls" />
                            )}
                          </div>

                          {landmine.triggerVariations && landmine.triggerVariations.length > 0 && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-ink-secondary">Variations: </span>
                              <span className="text-sm text-ink-secondary">{landmine.triggerVariations.join(', ')}</span>
                            </div>
                          )}

                          {landmine.quickTips && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-ink-secondary">
                                {landmine.landmineType === 'detractor' ? 'Say Instead: ' : 'Quick Tips: '}
                              </span>
                              <span className={`text-sm ${landmine.landmineType === 'detractor' ? 'text-ink font-medium' : 'text-ink-secondary'}`}>
                                {landmine.landmineType === 'detractor' ? `"${landmine.quickTips}"` : landmine.quickTips}
                              </span>
                            </div>
                          )}

                          {landmine.landmineType !== 'detractor' && (
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              {landmine.canSay && landmine.canSay.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-success mb-1">Can Say:</p>
                                  <ul className="text-xs text-ink-secondary list-disc list-inside">
                                    {landmine.canSay.slice(0, 2).map((item, idx) => (
                                      <li key={idx}>{item.point}</li>
                                    ))}
                                    {landmine.canSay.length > 2 && (
                                      <li className="text-ink-muted">+{landmine.canSay.length - 2} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}

                              {landmine.cannotSay && landmine.cannotSay.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-error mb-1">Cannot Say:</p>
                                  <ul className="text-xs text-ink-secondary list-disc list-inside">
                                    {landmine.cannotSay.slice(0, 2).map((item, idx) => (
                                      <li key={idx}>{item.point}</li>
                                    ))}
                                    {landmine.cannotSay.length > 2 && (
                                      <li className="text-ink-muted">+{landmine.cannotSay.length - 2} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {landmine.approvalStatus === 'pending' && (
                          <button
                            onClick={() => handleApproveLandmine(landmine)}
                            className="px-3 py-1 rounded text-sm border border-success bg-success-tint text-success hover:opacity-80"
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleLandmineStatus(landmine)}
                          className={`px-3 py-1 rounded text-sm ${
                            landmine.isActive
                              ? 'bg-surface-sunken text-ink-secondary hover:bg-surface-subtle hover:text-ink'
                              : 'bg-signature text-on-signature hover:bg-signature-strong'
                          }`}
                        >
                          {landmine.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEditLandmine(landmine)}
                          className="text-signature hover:text-signature-strong p-1"
                          title="Edit"
                        >
                          <PencilIcon style={{ fontSize: '1.25rem' }} />
                        </button>
                        <button
                          onClick={() => handleDeleteLandmine(landmine._id, landmine.trigger)}
                          className="p-1 text-error hover:opacity-80"
                          title="Delete landmine"
                        >
                          <TrashIcon style={{ fontSize: '1.25rem' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'competitors' ? (
          // Competitors display - moved here into main conditional
          <div className="p-4">
            <p className="text-sm text-ink-secondary mb-4">
              Who prospects compare you against, where you win, where they're weaker, and talking points reps can use.
            </p>
            <CompetitorManagement
              competitors={competitors}
              searchTerm={searchTerm}
              onRefresh={fetchItems}
              onEdit={(competitor) => {
                setEditingCompetitor(competitor);
                setIsCompetitorModalOpen(true);
              }}
              setError={setError}
              setSuccess={setSuccess}
              selectedCompetitorIds={selectedCompetitorIds}
              onToggleCompetitor={(competitorId: string) => {
                setSelectedCompetitorIds(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(competitorId)) {
                    newSet.delete(competitorId);
                  } else {
                    newSet.add(competitorId);
                  }
                  return newSet;
                });
              }}
              focusIncomplete={searchParams.get('focus') === 'incomplete-competitors'}
            />
          </div>
        ) : activeTab === 'salesplaybooks' ? (
          // Sales Playbooks display — answers are what reps say when asked
          // or pushed back on; playbooks are how reps guide the call itself.
          <SalesPlaybookManagement
            setError={setError}
            setSuccess={setSuccess}
            openAddSignal={openPlaybookEditorSignal}
            canGenerateFromPublishedKb={readiness.canCreatePlaybook}
            onReviewKnowledgeBaseDraft={
              readiness.activeDraftTask
                ? () => openGuidedSetup(readiness.activeDraftTask?._id)
                : undefined
            }
          />
        ) : displayItems.length === 0 ? (
          <div className="p-8 text-center text-ink-muted">
            {isSearchMode ? 'No results found. Try a different search term.' : 'No items found. Add a new item to get started.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-surface-subtle border-b border-hairline">
                <tr>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-ink w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-signature rounded border-ink-border focus:ring-signature"
                      checked={displayItems.length > 0 && displayItems.every(item => {
                        const allIds = [item._id, ...(item.variations?.map(v => v._id) || [])];
                        return allIds.every(id => selectedItemIds.has(id));
                      })}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Select all
                          const newSet = new Set(selectedItemIds);
                          displayItems.forEach(item => {
                            newSet.add(item._id);
                            item.variations?.forEach(v => newSet.add(v._id));
                          });
                          setSelectedItemIds(newSet);
                        } else {
                          // Deselect all
                          setSelectedItemIds(new Set());
                        }
                      }}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ink w-1/3">Question</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ink flex-1">Answer</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-ink w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {displayItems.map((item) => (
                  <React.Fragment key={item._id}>
                    {/* Main row */}
                    <tr
                      onClick={() => item.variations && item.variations.length > 0 && toggleExpand(item._id)}
                      className={`${item.variations && item.variations.length > 0 ? 'cursor-pointer hover:bg-signature-tint' : 'hover:bg-surface-subtle'} transition-colors`}
                    >
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedItemIds.has(item._id)}
                          onChange={() => handleToggleItem(item._id, item)}
                          className="h-4 w-4 text-signature rounded border-ink-border focus:ring-signature"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          {item.variations && item.variations.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(item._id);
                              }}
                              className="p-0.5 text-ink-muted hover:text-ink-secondary hover:bg-surface-sunken rounded transition-colors flex-shrink-0"
                              title={item.isExpanded ? "Collapse" : "Expand variations"}
                            >
                              {item.isExpanded ? <ExpandLessIcon style={{ fontSize: '1.25rem' }} /> : <ExpandMoreIcon style={{ fontSize: '1.25rem' }} />}
                            </button>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-ink break-words">{item.question}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {item.isOriginal ? (
                                <span className="px-2 py-1 text-xs bg-surface-sunken text-ink-secondary rounded-full">
                                  Main question
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs bg-surface-subtle text-ink-secondary rounded-full">
                                  Alternate phrasing
                                </span>
                              )}
                              {item.category && TOPIC_CATEGORY_LABELS[item.category as TopicCategory] && (
                                <span className="px-2 py-1 text-xs bg-surface-sunken text-ink-secondary rounded-full">
                                  {TOPIC_CATEGORY_LABELS[item.category as TopicCategory]}
                                </span>
                              )}
                              {item.variations && item.variations.length > 0 && (
                                <span className="text-xs text-ink-secondary bg-surface-subtle px-2 py-1 rounded-full">
                                  {item.variations.length} alternate phrasing{item.variations.length !== 1 ? 's' : ''}
                                </span>
                              )}
                              {/* Live-call readiness — an icon with a
                                  tooltip, matching how Landmines/Competitors/
                                  Playbooks show active status, instead of a
                                  standalone text chip. */}
                              {item.needsEmbedding ? (
                                <CloudSyncIcon className="text-warning" style={{ fontSize: '1rem' }} titleAccess="Preparing for live calls" />
                              ) : (
                                <CheckCircleIcon className="text-success" style={{ fontSize: '1rem' }} titleAccess="Available in live calls" />
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <p className="text-ink-secondary line-clamp-3 whitespace-pre-wrap break-words">{item.answer}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                            className="text-signature hover:text-signature-strong p-1"
                            title="Edit"
                          >
                            <PencilIcon style={{ fontSize: '1.25rem' }} />
                          </button>
                          {/* One entry point for a new phrasing — the modal
                              it opens offers both hand-typed rows and an
                              AI-generate action together, instead of forcing
                              a choice between two separate row buttons.
                              The compact icon matches the table's row actions. */}
                          {item.isOriginal && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddVariation(item);
                              }}
                              className="p-1.5 text-ink-muted hover:text-success hover:bg-success-tint rounded"
                              title="Add alternate phrasing"
                            >
                              <PlusIcon style={{ fontSize: '1.1rem' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Variations rows (shown when expanded) */}
                    {item.isExpanded && item.variations && item.variations.length > 0 ? (
                      <>
                        {item.variations.map((variation: DictionaryVariation) => (
                          <tr key={variation._id} className="bg-surface-subtle border-l-4 border-signature hover:bg-surface-sunken transition-colors">
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedItemIds.has(variation._id)}
                                onChange={() => handleToggleVariation(variation._id)}
                                disabled={selectedItemIds.has(item._id)}
                                className={`h-4 w-4 text-signature rounded border-ink-border focus:ring-signature ${
                                  selectedItemIds.has(item._id) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                title={selectedItemIds.has(item._id) ? 'This variation will be deleted with its parent' : undefined}
                              />
                            </td>
                            <td colSpan={3} className="px-4 py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-block px-2 py-1 text-xs bg-surface-sunken text-ink-secondary rounded-full font-medium">
                                      Alternate phrasing
                                    </span>
                                    {/* Live-call readiness */}
                                    {variation.needsEmbedding ? (
                                      <CloudSyncIcon className="text-warning" style={{ fontSize: '1rem' }} titleAccess="Preparing for live calls" />
                                    ) : (
                                      <CheckCircleIcon className="text-success" style={{ fontSize: '1rem' }} titleAccess="Available in live calls" />
                                    )}
                                  </div>
                                  <p className="text-sm text-ink-secondary"><strong>Question:</strong> {variation.question}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                                  <button
                                    onClick={() => {
                                      handleEdit({
                                        _id: variation._id,
                                        question: variation.question,
                                        answer: item.answer,
                                        category: '',
                                        isOriginal: false,
                                        parentId: item._id
                                      });
                                    }}
                                    className="text-signature hover:text-signature-strong p-1"
                                    title="Edit variation"
                                  >
                                    <PencilIcon style={{ fontSize: '1rem' }} />
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {/* Use search pagination when in search mode, otherwise use regular pagination */}
        {/* Hide pagination for tabs that aren't paginated (single-form tabs) */}
        {(() => {
          // Sales Playbooks manage their own list and have no pagination.
          if (activeTab === 'salesplaybooks') return null;

          const activePagination = isSearchMode ? searchPagination : pagination;
          const activePage = isSearchMode ? searchCurrentPage : currentPage;
          const setActivePage = isSearchMode
            ? (page: number) => handleSearch(searchTerm, page)
            : setCurrentPage;

          if (!activePagination) return null;
          const paginationLabel = activeTab === 'landmines'
            ? 'landmines'
            : activeTab === 'competitors'
              ? 'competitors'
              : activeTab === 'faqs' ? 'FAQs' : 'objections';

          return (
            <div className="px-4 py-2 bg-surface-subtle border-t border-hairline flex items-center justify-between">
              <div className="text-sm text-ink-secondary flex items-center gap-2">
                {isSearchMode && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-surface-sunken text-ink-secondary rounded-full">
                    <MagnifyingGlassIcon style={{ fontSize: '0.875rem' }} className="mr-1" />
                    Search Results
                  </span>
                )}
                <span>
                  {(activePagination?.total || 0) === 0
                    ? `No ${isSearchMode ? 'matching results' : paginationLabel} to show`
                    : `Showing ${((activePage - 1) * itemsPerPage) + 1} to ${Math.min(activePage * itemsPerPage, activePagination?.total || 0)} of ${activePagination?.total || 0} ${isSearchMode ? 'results' : paginationLabel}`}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setActivePage(Math.max(1, activePage - 1))}
                  disabled={activePage === 1}
                  className={`px-3 py-1 rounded ${
                    activePage === 1
                      ? 'bg-surface-sunken text-ink-muted cursor-not-allowed'
                      : 'bg-surface border border-ink-border text-ink-secondary hover:bg-surface-subtle'
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, activePagination?.totalPages || 1) }, (_, i) => {
                  let pageNum: number;
                  const totalPages = activePagination?.totalPages || 1;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (activePage <= 3) {
                    pageNum = i + 1;
                  } else if (activePage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = activePage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setActivePage(pageNum)}
                      className={`px-3 py-1 rounded ${
                        activePage === pageNum
                          ? 'kb-filter-chip is-active'
                          : 'bg-surface border border-ink-border text-ink-secondary hover:bg-surface-subtle'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setActivePage(Math.min(activePagination?.totalPages || 1, activePage + 1))}
                  disabled={activePage === (activePagination?.totalPages || 1)}
                  className={`px-3 py-1 rounded ${
                    activePage === (activePagination?.totalPages || 1)
                      ? 'bg-surface-sunken text-ink-muted cursor-not-allowed'
                      : 'bg-surface border border-ink-border text-ink-secondary hover:bg-surface-subtle'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          );
        })()}
      </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div
          data-testid="question-dialog-backdrop"
          className="modal-backdrop kb-modal-overlay"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeInlineDialogs(); }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="question-dialog-title" data-kb-modal="true" className="modal-panel kb-dialog max-w-2xl w-full max-h-[90vh]">
            <div className="p-6">
              <h2 id="question-dialog-title" className="heading-4 kb-dialog-title mb-4">
                {editingItem ?
                  (editingItem.isOriginal === false ? 'Edit Alternate Phrasing' : 'Edit Main Question')
                  : 'Add Main Question'
                } {activeTab === 'objections' ? '(Objection)' : '(FAQ)'}
              </h2>

              <form onSubmit={handleSubmit}>
                {editingItem && editingItem.isOriginal === false && (
                  <div className="mb-4 p-3 bg-info-tint border border-info rounded-lg">
                    <p className="text-sm text-ink">
                      <strong>Note:</strong> Alternate phrasings share the same answer as their main question.
                      Editing the answer here will update it everywhere this question is asked.
                    </p>
                  </div>
                )}

                {polishFeedback && (
                  <div role="status" className="mb-4 p-3 bg-info-tint border border-info rounded-lg text-sm text-ink">
                    {polishFeedback}
                  </div>
                )}

                <div className="mb-4">
                  <label htmlFor="knowledge-question" className="block text-sm font-medium text-ink-secondary mb-2">
                    Question
                  </label>
                  <textarea
                    id="knowledge-question"
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                    rows={3}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="knowledge-answer" className="block text-sm font-medium text-ink-secondary mb-2">
                    Answer {editingItem && editingItem.isOriginal === false && '(Shared with parent)'}
                  </label>
                  <textarea
                    id="knowledge-answer"
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                    rows={5}
                    required
                    disabled={!!(editingItem && editingItem.isOriginal === false)}
                  />
                </div>

                <div className="mb-6">
                  <label id="knowledge-category-label" className="block text-sm font-medium text-ink-secondary mb-2">
                    Topic Category {editingItem && editingItem.isOriginal === false && '(Shared with parent)'}
                  </label>
                  <Dropdown
                    id="knowledge-category"
                    labelledBy="knowledge-category-label"
                    width="field"
                    value={formData.category}
                    onChange={(category) => setFormData({ ...formData, category })}
                    disabled={!!(editingItem && editingItem.isOriginal === false)}
                    options={getAllTopicCategories().map((cat) => ({ value: cat, label: TOPIC_CATEGORY_LABELS[cat] }))}
                  />
                </div>

                <div className="flex justify-between gap-3">
                  {!(editingItem && editingItem.isOriginal === false) && <button
                    type="button"
                    onClick={handlePolishAnswer}
                    disabled={isSubmitting || isPolishingAnswer}
                    className="kb-button kb-button-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPolishingAnswer ? (
                      <ArrowPathIcon className="animate-spin" style={{ fontSize: '1rem' }} />
                    ) : (
                      <PolishIcon style={{ fontSize: '1rem' }} />
                    )}
                    {isPolishingAnswer ? 'Polishing...' : 'Polish answer with AI'}
                  </button>}
                  <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSubmitting}
                    className="kb-button kb-button-secondary px-4 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="kb-button kb-button-primary px-4 py-2"
                  >
                    {isSubmitting && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting ? (editingItem ? 'Updating...' : 'Adding...') : (editingItem ? 'Update' : 'Add')}
                  </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Landmine Add/Edit Modal */}
      {isLandmineModalOpen && (
        <div
          className="modal-backdrop kb-modal-overlay"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeInlineDialogs(); }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="landmine-dialog-title" data-kb-modal="true" className="modal-panel kb-dialog max-w-4xl w-full max-h-[calc(90vh-100px)] flex flex-col">
            {/* Fixed Header */}
            <div className="kb-dialog-header flex-shrink-0">
              <h2 id="landmine-dialog-title" className="heading-4 kb-dialog-title">
                {editingLandmine ? 'Edit Landmine' : 'Add New Landmine'}
              </h2>
            </div>

            <form onSubmit={handleLandmineSubmit} className="flex flex-col flex-1 overflow-hidden">
              {/* Scrollable Content */}
              <div className="kb-dialog-body overflow-y-auto flex-1">
                {/* Error Display */}
                {landmineModalError && (
                  <div className="mb-4 bg-error-tint border border-error rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-error mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-ink mb-1">Error</h3>
                        <p className="text-sm text-ink-secondary">{landmineModalError}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setLandmineModalError(null)}
                        className="text-error hover:opacity-80"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-4">
                  <div>
                    <label htmlFor="landmine-trigger" className="block text-sm font-medium text-ink-secondary mb-1">
                      Trigger Word/Phrase <span className="text-error">*</span>
                    </label>
                    <input
                      id="landmine-trigger"
                      type="text"
                      value={landmineFormData.trigger || ''}
                      onChange={(e) => setLandmineFormData({ ...landmineFormData, trigger: e.target.value })}
                      className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                      placeholder={landmineFormData.landmineType === 'detractor' ? "e.g., I understand, Trust me" : "e.g., FDA approved, guaranteed, cure"}
                      required
                    />
                  </div>

                  <div>
                    <label id="landmine-type-label" className="block text-sm font-medium text-ink-secondary mb-1">
                      Type <span className="text-error">*</span>
                    </label>
                    <Dropdown
                      id="landmine-type"
                      labelledBy="landmine-type-label"
                      width="field"
                      value={landmineFormData.landmineType || 'legal_risk'}
                      onChange={(landmineType) => setLandmineFormData({
                        ...landmineFormData,
                        landmineType: landmineType as 'legal_risk' | 'detractor',
                        // Reset type-specific fields when switching
                        priority: landmineType === 'detractor' ? 'high' : landmineFormData.priority,
                        category: landmineType === 'detractor' ? '' : landmineFormData.category
                      })}
                      options={LANDMINE_TYPE_OPTIONS}
                    />
                    <p className="text-xs text-ink-muted mt-1">
                      {landmineFormData.landmineType === 'detractor'
                        ? 'Phrases that negatively impact sales conversations'
                        : 'Compliance and legal risk phrases'}
                    </p>
                  </div>

                  {landmineFormData.landmineType === 'legal_risk' && (
                    <div>
                      <label htmlFor="landmine-category" className="block text-sm font-medium text-ink-secondary mb-1">
                        Category <span className="text-error">*</span>
                      </label>
                      <input
                        id="landmine-category"
                        type="text"
                        value={landmineFormData.category || ''}
                        onChange={(e) => setLandmineFormData({ ...landmineFormData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                        placeholder="e.g., Medical Claims, Legal Terms, Pricing"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label id="landmine-priority-label" className="block text-sm font-medium text-ink-secondary mb-1">
                      Priority <span className="text-error">*</span>
                    </label>
                    <Dropdown
                      id="landmine-priority"
                      labelledBy="landmine-priority-label"
                      width="field"
                      value={landmineFormData.priority || 'medium'}
                      onChange={(priority) => setLandmineFormData({ ...landmineFormData, priority: priority as 'high' | 'medium' | 'low' })}
                      disabled={landmineFormData.landmineType === 'detractor'}
                      options={LANDMINE_PRIORITY_OPTIONS}
                    />
                    {landmineFormData.landmineType === 'detractor' && (
                      <p className="text-xs text-ink-muted mt-1">Detractors are always high priority</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="landmine-trigger-variation" className="block text-sm font-medium text-ink-secondary">
                      Trigger Variations
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerateTriggerVariations}
                      disabled={generatingVariations || !landmineFormData.trigger?.trim()}
                      className="px-3 py-1 text-sm border border-ink-border bg-surface text-ink-secondary rounded-lg hover:bg-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {generatingVariations ? (
                        <>
                          <ArrowPathIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
                          Generating...
                        </>
                      ) : (
                        <>Generate variations</>
                      )}
                    </button>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <input
                      id="landmine-trigger-variation"
                      type="text"
                      placeholder="Add variation manually... (Press Enter)"
                      value={triggerVariationInput}
                      onChange={(e) => setTriggerVariationInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddTriggerVariation();
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                    />
                    <button
                      type="button"
                      onClick={handleAddTriggerVariation}
                      className="kb-dialog-compact-action px-4 py-2 rounded-lg"
                    >
                      Add
                    </button>
                  </div>
                  {(landmineFormData.triggerVariations || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {(landmineFormData.triggerVariations || []).map((variation, index) => (
                        <span
                          key={index}
                          className="kb-dialog-chip inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                        >
                          {variation}
                          <button
                            type="button"
                            onClick={() => handleDeleteTriggerVariation(variation)}
                            className="hover:text-signature-strong"
                          >
                            <CloseIcon style={{ fontSize: '0.875rem' }} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-ink-muted mt-2">
                    Variations help detect different ways users might say the trigger phrase
                  </p>
                </div>

                <div className="mb-4">
                  <label htmlFor="landmine-quick-tips" className="block text-sm font-medium text-ink-secondary mb-2">
                    {landmineFormData.landmineType === 'detractor' ? 'Corrective Phrase' : 'Quick Tips'}
                    {landmineFormData.landmineType === 'detractor' && <span className="text-error"> *</span>}
                  </label>
                  <textarea
                    id="landmine-quick-tips"
                    value={landmineFormData.quickTips || ''}
                    onChange={(e) => setLandmineFormData({ ...landmineFormData, quickTips: e.target.value })}
                    className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                    rows={2}
                    placeholder={landmineFormData.landmineType === 'detractor'
                      ? "What to say instead (e.g., 'Help me understand what matters most here')"
                      : "Brief guidance or context for this landmine"}
                    required={landmineFormData.landmineType === 'detractor'}
                  />
                  {landmineFormData.landmineType === 'detractor' && (
                    <p className="text-xs text-ink-muted mt-1">This phrase will be shown as the recommended alternative</p>
                  )}
                </div>

                {landmineFormData.landmineType === 'legal_risk' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <div className="kb-dialog-section-label block text-sm font-medium mb-2">What reps can say</div>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {(landmineFormData.canSay || []).map((item, index) => (
                        <div key={index} className="border border-hairline rounded-lg p-2">
                          <div className="flex gap-2 mb-2">
                            <input
                              aria-label={`Approved phrase ${index + 1}`}
                              type="text"
                              value={item.point}
                              onChange={(e) => {
                                const newCanSay = [...(landmineFormData.canSay || [])];
                                newCanSay[index] = { ...newCanSay[index], point: e.target.value };
                                setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                              }}
                              className="flex-1 px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                              placeholder="Approved phrase"
                            />
                            <button
                              aria-label={`Remove approved phrase ${index + 1}`}
                              type="button"
                              onClick={() => {
                                const newCanSay = (landmineFormData.canSay || []).filter((_, i) => i !== index);
                                setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                              }}
                              className="kb-dialog-icon-danger px-2 py-1 rounded"
                            >
                              ×
                            </button>
                          </div>
                          <input
                            aria-label={`Approved phrase ${index + 1} context`}
                            type="text"
                            value={item.context || ''}
                            onChange={(e) => {
                              const newCanSay = [...(landmineFormData.canSay || [])];
                              newCanSay[index] = { ...newCanSay[index], context: e.target.value };
                              setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                            }}
                            className="w-full px-3 py-1 border border-ink-border rounded text-sm focus:ring-signature focus:border-signature"
                            placeholder="Context (optional)"
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newCanSay = [...(landmineFormData.canSay || []), { point: '', context: '' }];
                          setLandmineFormData({ ...landmineFormData, canSay: newCanSay });
                        }}
                        className="kb-dialog-add-action px-4 py-2 rounded-lg w-full"
                      >
                        + Add Approved Phrase
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="kb-dialog-section-label is-caution block text-sm font-medium mb-2">What reps cannot say</div>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {(landmineFormData.cannotSay || []).map((item, index) => (
                        <div key={index} className="border border-hairline rounded-lg p-2">
                          <div className="flex gap-2 mb-2">
                            <input
                              aria-label={`Prohibited phrase ${index + 1}`}
                              type="text"
                              value={item.point}
                              onChange={(e) => {
                                const newCannotSay = [...(landmineFormData.cannotSay || [])];
                                newCannotSay[index] = { ...newCannotSay[index], point: e.target.value };
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="flex-1 px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                              placeholder="Prohibited phrase"
                            />
                            <button
                              aria-label={`Remove prohibited phrase ${index + 1}`}
                              type="button"
                              onClick={() => {
                                const newCannotSay = (landmineFormData.cannotSay || []).filter((_, i) => i !== index);
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="kb-dialog-icon-danger px-2 py-1 rounded"
                            >
                              ×
                            </button>
                          </div>
                          <div className="flex gap-2">
                            <input
                              aria-label={`Prohibited phrase ${index + 1} reason`}
                              type="text"
                              value={item.reason || ''}
                              onChange={(e) => {
                                const newCannotSay = [...(landmineFormData.cannotSay || [])];
                                newCannotSay[index] = { ...newCannotSay[index], reason: e.target.value };
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              className="flex-1 px-3 py-1 border border-ink-border rounded text-sm focus:ring-signature focus:border-signature"
                              placeholder="Reason (optional)"
                            />
                            {/* Narrow: it shares the row with the reason input, which takes the rest. */}
                            <Dropdown
                              ariaLabel={`Prohibited phrase ${index + 1} severity`}
                              width="narrow"
                              value={item.severity || 'general'}
                              onChange={(severity) => {
                                const newCannotSay = [...(landmineFormData.cannotSay || [])];
                                newCannotSay[index] = { ...newCannotSay[index], severity: severity as 'general' | 'legal_risk' | 'compliance_risk' | 'brand_risk' };
                                setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                              }}
                              options={PROHIBITED_PHRASE_SEVERITY_OPTIONS}
                            />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newCannotSay = [...(landmineFormData.cannotSay || []), { point: '', reason: '', severity: 'general' as 'general' | 'legal_risk' | 'compliance_risk' | 'brand_risk' }];
                          setLandmineFormData({ ...landmineFormData, cannotSay: newCannotSay });
                        }}
                        className="kb-dialog-add-action is-caution px-4 py-2 rounded-lg w-full"
                      >
                        + Add Prohibited Phrase
                      </button>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Fixed Footer */}
              <div className="kb-dialog-footer flex-shrink-0 bg-surface">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsLandmineModalOpen(false)}
                    disabled={savingLandmine}
                    className="kb-button kb-button-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingLandmine}
                    className="kb-button kb-button-primary px-4 py-2"
                  >
                    {savingLandmine ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>{editingLandmine ? 'Update' : 'Add'} Landmine</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generated Variations Review Modal */}
      {isGeneratedVariationsModalOpen && selectedParent && (
        <div
          className="modal-backdrop kb-modal-overlay"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeInlineDialogs(); }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="generated-variations-dialog-title" data-kb-modal="true" className="modal-panel kb-dialog max-w-3xl w-full max-h-[90vh]">
            <div className="p-6">
              <h2 id="generated-variations-dialog-title" className="heading-4 kb-dialog-title mb-4">Add alternate phrasings</h2>

              <div className="mb-4 p-4 bg-surface-subtle rounded-lg">
                <p className="text-sm font-medium text-ink-secondary mb-1">Main question:</p>
                <p className="text-sm text-ink-secondary mb-2">{selectedParent.question}</p>
                <p className="text-sm font-medium text-ink-secondary mb-1">Answer:</p>
                <p className="text-sm text-ink-secondary">{selectedParent.answer}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-ink-secondary mb-2">
                  Write your own, generate a few with AI, or both — edit or remove any line before saving:
                </p>
                <div className="space-y-3">
                  {generatedVariations.map((variation, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-sm text-ink-muted mt-2">{index + 1}.</span>
                      <textarea
                        aria-label={`Alternate phrasing ${index + 1}`}
                        value={variation}
                        onChange={(e) => {
                          const newVariations = [...generatedVariations];
                          newVariations[index] = e.target.value;
                          setGeneratedVariations(newVariations);
                        }}
                        className="flex-1 px-3 py-2 border border-ink-border rounded-lg focus:ring-signature focus:border-signature"
                        rows={2}
                      />
                      <button
                        onClick={() => {
                          const newVariations = generatedVariations.filter((_, i) => i !== index);
                          setGeneratedVariations(newVariations);
                        }}
                        className="text-error hover:opacity-80 p-1"
                        title="Remove variation"
                      >
                        <TrashIcon style={{ fontSize: '1.25rem' }} />
                      </button>
                    </div>
                  ))}
                </div>

                {generatedVariations.length === 0 && (
                  <p className="text-sm text-ink-muted text-center py-4">
                    All variations have been removed. Click cancel to close without saving.
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const newVariation = '';
                      setGeneratedVariations([...generatedVariations, newVariation]);
                    }}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-surface-subtle text-ink-secondary rounded-lg hover:bg-surface-sunken"
                  >
                    <PlusIcon style={{ fontSize: '1rem' }} />
                    Add Another
                  </button>
                  {!isSearchMode && (
                    <button
                      onClick={() => handleGenerateVariations(selectedParent)}
                      disabled={generatingFor === selectedParent._id}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-surface-subtle text-ink-secondary rounded-lg hover:bg-surface-sunken disabled:opacity-60"
                      title="Generate a few more with AI"
                    >
                      {generatingFor === selectedParent._id ? (
                        <ArrowPathIcon className="animate-spin" style={{ fontSize: '1rem' }} />
                      ) : (
                        <PolishIcon style={{ fontSize: '1rem' }} />
                      )}
                      {generatingFor === selectedParent._id ? 'Generating…' : 'Generate with AI'}
                    </button>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsGeneratedVariationsModalOpen(false);
                      setGeneratedVariations([]);
                    }}
                    className="kb-button kb-button-secondary px-4 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveGeneratedVariations}
                    disabled={savingVariations || generatedVariations.length === 0 || generatedVariations.every(v => !v.trim())}
                    className="kb-button kb-button-primary px-4 py-2"
                  >
                    {savingVariations ? (
                      <>
                        <ArrowPathIcon className="animate-spin" style={{ fontSize: '1rem' }} />
                        Saving...
                      </>
                    ) : (
                      <>Save {generatedVariations.filter(v => v.trim()).length} Phrasing{generatedVariations.filter(v => v.trim()).length !== 1 ? 's' : ''}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Competitor Modal */}
      <CompetitorModal
        isOpen={isCompetitorModalOpen}
        onClose={() => {
          setIsCompetitorModalOpen(false);
          setEditingCompetitor(null);
        }}
        competitor={editingCompetitor}
        onSuccess={fetchItems}
        setSuccess={setSuccess}
      />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </MainLayout>
  );
};

export default KnowledgeBase;
